// ============================================================
// BOOTH Bookings — processing fee model (single source of truth)
// ------------------------------------------------------------
// Spec: "BOOTH — Processing Fee Pass-Through" (Eduardo, ready-to-build).
//
//   • BOOTH charges the CLIENT no platform percentage and no per-booking fee:
//     the only thing added to the client's total is Stripe's real processing
//     cost, and only when the barber turns pass-through on (below). BOOTH's own
//     revenue is BOOKINGS_FEE_RATE, taken as a Connect application fee out of
//     the BARBER's side of the charge, on the base price only — never a markup
//     on the client's processing line.
//
//   • DEFAULT — barber ABSORBS processing (`pass_processing_to_client = false`):
//     the client pays exactly the barber's listed price (one clean number).
//     "Absorbs" here means the barber forgoes the gross-up, NOT that Stripe bills
//     them: on a destination charge Stripe's fee is debited from the PLATFORM's
//     balance (controller.fees.payer = "application_express" on every BOOTH
//     Express account), so BOOTH pays it either way. The barber's payout is
//     base − BOOTH fee, untouched by Stripe's cut. No processing line is shown.

//   • WHO PAYS WHAT (verified against live balance transactions, 2026-10-01):
//       client    → base (+ a "Processing" line only in pass-through mode)
//       barber    → receives base − BOOTH fee; never billed Stripe's cut
//       BOOTH     → collects the application fee, pays Stripe processing out of
//                   it, and pays Connect account/payout/volume fees on top.
//     `on_behalf_of` sets the statement descriptor and the pricing basis. It does
//     NOT move processing liability — a comment claiming otherwise was wrong in
//     six routes and is corrected in all of them.
//
//   • OPTIONAL — barber turns PASS-THROUGH on (`pass_processing_to_client = true`):
//     a transparent "Processing" line is added to the client's total, sized so
//     the barber nets their FULL listed price. The line is ALWAYS labelled
//     `PROCESSING_LINE_LABEL` ("Processing") — never "Booking fee", "Convenience
//     fee", or "Service fee". That label rule is a hard requirement, not cosmetic
//     (it's the line Squire crossed that drew barber complaints).
//
// The barber-side toggle exists because barbers own their pricing absolutely.
// Most are expected to leave it OFF.
//
// Client-safe (pure math, no secrets) — importable in client or server.
// ============================================================

import { INTERNATIONAL_CARD_PERCENT, CURRENCY_CONVERSION_PERCENT } from "@/lib/connect-costs"

/**
 * BOOTH's cut when a client pays a barber through BOOTH Bookings — the single
 * source of truth, and NOT the booth-rent fee (lib/platform-fee). The two are
 * separate rates on purpose: they have moved independently before, and the day
 * one of them changes must not silently change the other.
 *
 * It was `boothRentPlatformFeeCents` in the appointment path and a local
 * `BOOTH_FEE_PERCENT = 3` in the membership path, so the same "3% on bookings"
 * was billing 5% on deposits and 3% on memberships.
 *
 * Raised from 3% to 5% on October 2, 2026. It now equals the booth-rent rate,
 * but stays its own constant so either can move without dragging the other.
 *
 * 5% alone still loses money on small tickets: BOOTH pays Stripe's 2.9% + $0.30
 * out of this fee, so break-even is 0.05·S = 0.029·S + 0.30 ⇒ S = $14.29. The
 * per-currency minimum below is what keeps every booking above water.
 *
 * This is the DOMESTIC rate. A charge that costs BOOTH Stripe's international
 * or conversion surcharge prices at BOOKINGS_FEE_RATE_SURCHARGED instead; ask
 * bookingsFeeRate(currency) rather than reading this constant directly.
 */
export const BOOKINGS_FEE_RATE = 0.05

/** For display: "5%". Derive UI copy from the rate, never retype it. */
export const BOOKINGS_FEE_LABEL = `${Math.round(BOOKINGS_FEE_RATE * 100)}%`

/** Stripe's `application_fee_percent` wants a number, not a ratio. */
export const BOOKINGS_FEE_PERCENT = BOOKINGS_FEE_RATE * 100

/**
 * SURCHARGED RATE — what BOOTH charges when a payment costs BOOTH more.
 *
 * Stripe's US rate card adds 1.5% for a card issued outside the US and a
 * further 1% when it converts currency. BOOTH pays both out of its own fee, so
 * at the flat 5% a surcharged charge is loss-making at EVERY price: the cost
 * floor alone is 2.9% + 1.5% + 1% = 5.4% before the fixed component.
 *
 * Derived from the cost constants rather than typed, so the day Stripe moves a
 * surcharge the price moves with it. Today: 5% + 1.5% + 1% = 7.5%, which leaves
 * the same ~2.1% net as a domestic USD charge rather than a thicker margin —
 * this is cost recovery, not a premium for being foreign.
 *
 * WHAT THIS DOES AND DOES NOT COVER. The settlement currency is known when the
 * PaymentIntent is created, so a non-USD charge is priced correctly here. A
 * card issued abroad paying USD is NOT knowable at that point — Stripe only
 * reports `payment_method_details.card.country` after the charge — so those
 * still pay the domestic 5% and the daily payments report flags them (#393).
 * Pricing that case needs the card inspected between confirm and capture; the
 * rate it would use is this constant, already here and already tested.
 */
export const BOOKINGS_FEE_RATE_SURCHARGED =
  BOOKINGS_FEE_RATE + INTERNATIONAL_CARD_PERCENT + CURRENCY_CONVERSION_PERCENT

/** For display: "7.5%". Derive UI copy from the rate, never retype it. */
export const BOOKINGS_FEE_SURCHARGED_LABEL = `${Number((BOOKINGS_FEE_RATE_SURCHARGED * 100).toFixed(2))}%`

/**
 * How many times the minimum fee a service must cost before BOOTH will take it
 * ONLINE. At 5x, the minimum is at most a fifth of what the barber earns:
 *
 *   $1 minimum, $5 floor -> fee is 20% of the booking at the floor, less above it
 *
 * One multiple for every currency, so a new currency cannot arrive with a
 * sensible minimum and a nonsense floor. USD's $1/$5 pair is exactly this rule.
 */
export const ONLINE_FLOOR_MULTIPLE_OF_MINIMUM = 5

export interface CurrencyFeeConfig {
  /** Minimum BOOTH fee, in this currency's MINOR units. */
  minimumMinor: number
  /**
   * True when a charge in this currency ALWAYS costs BOOTH the international
   * and conversion surcharges. BOOTH settles in USD, so anything else converts,
   * and a card paying in its own non-USD currency is foreign-issued in
   * practice. Those charges price at {@link BOOKINGS_FEE_RATE_SURCHARGED}.
   */
  surcharged: boolean
  /** Why this minimum, in this currency. Read before changing a number. */
  note: string
}

/**
 * Per-currency fee configuration, keyed by lowercase ISO code.
 *
 * A currency ABSENT from this map is UNPRICED: BOOTH will not take it online at
 * all (see {@link isOnlinePaymentAvailable}) and the booking falls through to
 * pay-in-person. That is deliberate and it is the conservative direction — a
 * minimum is a FIXED amount, and the right fixed amount depends on that
 * currency's Stripe rate card and local price levels. Porting "100" across
 * currencies would be a silent repricing in each one; charging a bare
 * percentage with no minimum is how a small booking ends up costing BOOTH more
 * than it earns. Refusing the charge loses nobody money.
 *
 * The non-USD minimums below are NOT FX conversions — BOOTH holds no rate and
 * must not invent one. Each is a round local amount chosen to sit comfortably
 * ABOVE $1 USD at any rate that currency has traded at in years, with the
 * implied USD value written next to it. They are floors, not prices: above the
 * minimum every currency pays its percentage.
 *
 * Which currencies are here is driven by where BOOTH actually has shops (see
 * lib/markets.ts — production holds Ontario and Ciudad de México rows), not by
 * a wishlist. Adding one is a review, not a line.
 */
export const CURRENCY_FEES: Record<string, CurrencyFeeConfig> = {
  usd: {
    minimumMinor: 100, // $1.00 — break-even on 5% is $14.29, so this covers the small end
    surcharged: false,
    note: "Home currency. 2.9% + $0.30, no conversion, domestic cards.",
  },
  cad: {
    minimumMinor: 150, // C$1.50 — ~$1.05 USD at 0.70, ~$1.20 at 0.80
    surcharged: true,
    note: "Converts to USD and the card is Canadian-issued: 2.9% + 1.5% + 1%.",
  },
  mxn: {
    minimumMinor: 2500, // $25 MXN — ~$1.25 USD at 20/USD, ~$1.14 at 22/USD
    surcharged: true,
    note: "Converts to USD and the card is Mexican-issued: 2.9% + 1.5% + 1%.",
  },
}

/** This currency's config, or null when BOOTH has not priced it. */
export function currencyFeeConfig(currency: string = "usd"): CurrencyFeeConfig | null {
  return CURRENCY_FEES[currency.trim().toLowerCase()] ?? null
}

/**
 * Whether BOOTH will take ONLINE payment in this currency at all.
 *
 * False for an unpriced currency. The caller falls through to its existing
 * pay-in-person path — the same one a barber with deposits off already uses —
 * so no booking is lost and nothing is rejected.
 */
export function isOnlinePaymentAvailable(currency: string = "usd"): boolean {
  return currencyFeeConfig(currency) !== null
}

/**
 * The fee rate in force for a currency: the flat rate at home, the surcharged
 * rate where Stripe's own cost is higher.
 *
 * An unpriced currency gets the surcharged rate, not the flat one. It should
 * never be charged at all, but if some future path charges one anyway, the
 * safe default is the rate that covers the higher cost.
 */
export function bookingsFeeRate(currency: string = "usd"): number {
  const cfg = currencyFeeConfig(currency)
  if (!cfg) return BOOKINGS_FEE_RATE_SURCHARGED
  return cfg.surcharged ? BOOKINGS_FEE_RATE_SURCHARGED : BOOKINGS_FEE_RATE
}

/**
 * LEGACY SHAPE, kept so nothing that reads the map by hand breaks. The config
 * above is the source of truth; this is a projection of it.
 */
export const BOOKINGS_FEE_MINIMUM_MINOR: Record<string, number> = Object.fromEntries(
  Object.entries(CURRENCY_FEES).map(([code, cfg]) => [code, cfg.minimumMinor]),
)

/** The minimum for a currency in minor units; 0 when none is configured. */
export function bookingsFeeMinimumMinor(currency: string = "usd"): number {
  return currencyFeeConfig(currency)?.minimumMinor ?? 0
}

export const BOOKINGS_FEE_MINIMUM_LABEL = `$${(BOOKINGS_FEE_MINIMUM_MINOR.usd / 100).toFixed(0)}`

/**
 * RECOMMENDATION, NOT ENFORCEMENT: the lowest service price at which the $1
 * minimum is a defensible share of what the barber earns.
 *
 *   $1.00 service -> fee $1.00  (100% — the barber keeps nothing)
 *   $2.00 service -> fee $1.00  (50%)
 *   $5.00 service -> fee $1.00  (20%)
 *  $10.00 service -> fee $1.00  (10%)
 *  $20.00 service -> fee $1.00  (5% — the minimum stops binding here)
 *
 * The calculator caps the fee at the booking price so a barber can never owe
 * money, but a 100% cap is still a booking that pays the barber nothing. Rather
 * than silently taking everything, BOOTH should decline to take online payment
 * below this price and let the barber collect in person.
 *
 * ENFORCED for ONLINE payment as of 2026-10-02. A service priced below this
 * does not get a PaymentIntent; the booking falls through to the route's
 * existing pay-in-person path (`amount: 0`), which is the same thing that
 * happens when a barber has deposits off. Nothing is rejected and no barber
 * loses a booking — they collect at the chair, as they already can.
 *
 * Enforced for EVERY currency BOOTH has priced, derived from that currency's
 * own minimum via ONLINE_FLOOR_MULTIPLE_OF_MINIMUM. A currency BOOTH has NOT
 * priced has no floor to compute, so online payment is refused outright rather
 * than charged a bare percentage that loses money on a small ticket.
 */
export const RECOMMENDED_MINIMUM_BOOKING_CENTS =
  CURRENCY_FEES.usd.minimumMinor * ONLINE_FLOOR_MULTIPLE_OF_MINIMUM

/** For display: "$5". Derive UI copy from the constant, never retype it. */
export const MINIMUM_ONLINE_BOOKING_LABEL = `$${(RECOMMENDED_MINIMUM_BOOKING_CENTS / 100).toFixed(0)}`

/**
 * The lowest price BOOTH will take ONLINE payment for, in minor units.
 *
 * 0 for an unpriced currency — not "no floor" but "no online payment at all";
 * ask {@link isOnlinePaymentAvailable} rather than reading 0 as permission.
 */
export function minimumOnlineBookingMinor(currency: string = "usd"): number {
  const cfg = currencyFeeConfig(currency)
  return cfg ? cfg.minimumMinor * ONLINE_FLOOR_MULTIPLE_OF_MINIMUM : 0
}

/**
 * True when BOOTH should not take this charge online.
 *
 * Two reasons, one answer, because every caller does the same thing with it
 * (fall through to pay-in-person): the price is under the currency's floor, or
 * BOOTH has not priced the currency at all.
 */
export function isBelowOnlineMinimum(baseCents: number, currency: string = "usd"): boolean {
  const base = Math.max(0, Math.round(baseCents))
  if (base <= 0) return false
  if (!isOnlinePaymentAvailable(currency)) return true
  return base < minimumOnlineBookingMinor(currency)
}

/** Which rule set the fee — useful for receipts, tests and support answers. */
export type BookingsFeeBasis = "percent" | "minimum" | "capped_at_base"

export interface BookingsFeeBreakdown {
  /** The base the fee is charged on, in minor units (never the processing line). */
  baseCents: number
  /** BOOTH's application fee, in minor units. */
  feeCents: number
  /** What the barber receives from this charge, in minor units. */
  barberNetCents: number
  /** Lowercase ISO currency this was computed for. */
  currency: string
  /** Minimum in force for that currency, in minor units (0 = none configured). */
  minimumCents: number
  /** Fee as a share of the base, 0–1. 1 means the barber keeps nothing. */
  feeShareOfBase: number
  /** The rate actually applied — flat or surcharged. On a receipt, show this. */
  rate: number
  /** True when the surcharged rate was used because the charge costs more. */
  surcharged: boolean
  basis: BookingsFeeBasis
}

/**
 * BOOTH's fee for a booking's BASE price — the single source of truth for every
 * charge path. Integer minor units in, integer minor units out; rounded exactly
 * once, here, so checkout, the PaymentIntent, capture and the receipt can never
 * disagree by a cent.
 *
 * `max(round(base × rate), minimum)`, then clamped to the base so the fee can
 * never exceed the charge it is taken from (Stripe rejects an application fee
 * larger than the charge, and a barber must never owe money on a booking).
 *
 * @param baseCents  the service/deposit price in minor units — NEVER the client
 *                   total, which may include a pass-through processing line.
 * @param currency   ISO code; decides which minimum applies.
 */
export function computeBookingsFee(baseCents: number, currency: string = "usd"): BookingsFeeBreakdown {
  const code = currency.trim().toLowerCase()
  const base = Math.max(0, Math.round(baseCents))
  const minimum = bookingsFeeMinimumMinor(code)

  if (base <= 0) {
    return {
      baseCents: 0, feeCents: 0, barberNetCents: 0, currency: code,
      minimumCents: minimum, feeShareOfBase: 0,
      rate: bookingsFeeRate(code), surcharged: bookingsFeeRate(code) !== BOOKINGS_FEE_RATE,
      basis: "percent",
    }
  }

  const rate = bookingsFeeRate(code)
  const percentFee = Math.round(base * rate)
  let fee = Math.max(percentFee, minimum)
  let basis: BookingsFeeBasis = fee === percentFee ? "percent" : "minimum"

  // A service priced below the minimum must not produce a fee above the charge.
  if (fee > base) {
    fee = base
    basis = "capped_at_base"
  }

  return {
    baseCents: base,
    feeCents: fee,
    barberNetCents: base - fee,
    currency: code,
    minimumCents: minimum,
    feeShareOfBase: fee / base,
    rate,
    surcharged: rate !== BOOKINGS_FEE_RATE,
    basis,
  }
}

/**
 * BOOTH's fee in minor units for a booking's BASE price. Thin wrapper over
 * {@link computeBookingsFee} for the charge paths, which only need the number.
 */
export function boothBookingsFeeCents(baseCents: number, currency: string = "usd"): number {
  return computeBookingsFee(baseCents, currency).feeCents
}

/**
 * BOOTH's fee expressed as Stripe's `application_fee_percent`, for the paths
 * that can only carry a percent (subscriptions: auto-renew rent, memberships).
 *
 * Derived from {@link computeBookingsFee} so a subscription and a one-off
 * booking of the same price collect the SAME money. Ceilinged at 2dp — Stripe's
 * precision — so rounding can never land under the minimum, and capped at 100%.
 *
 * The number returned is an implementation detail of the charge, not a figure to
 * show anyone: on a $7 membership it reads 14.29%, which is simply how "$1
 * minimum" has to be written when the only available unit is a percentage. The
 * disclosure stays "BOOKINGS_FEE_LABEL, minimum BOOKINGS_FEE_MINIMUM_LABEL",
 * which is what is actually collected.
 */
export function bookingsFeeApplicationPercent(priceCents: number, currency: string = "usd"): number {
  const price = Math.max(0, Math.round(priceCents))
  if (price <= 0) return BOOKINGS_FEE_PERCENT
  const fee = computeBookingsFee(price, currency).feeCents
  return Math.min(100, Math.ceil((fee / price) * 10000) / 100)
}

/**
 * What Stripe will actually collect when it applies that percent — i.e. the fee
 * a subscriber really pays. Used to prove displayed fee == collected fee.
 */
export function bookingsFeeCollectedFromPercent(priceCents: number, percent: number): number {
  return Math.round(Math.max(0, Math.round(priceCents)) * (percent / 100))
}

// The ONLY string allowed for the client-facing processing line. Import this
// everywhere it renders (checkout, receipt, email) so it can never drift.
export const PROCESSING_LINE_LABEL = "Processing"

// Transaction channel. `online` = card-not-present (web prepay, built first).
// `card_present` = in-person Tap to Pay / Terminal (native app track) — wired
// into the config now so the day Tap to Pay ships, the rate is already correct.
export type FeeChannel = "online" | "card_present"

// Stripe's published US rates, kept in config (NOT hardcoded inline) so they can
// be updated in one place when Stripe changes them. Verify the card_present rate
// before the in-person launch — Tap to Pay pricing can differ by program/region.
export const STRIPE_RATES: Record<FeeChannel, { percent: number; fixedCents: number }> = {
  online: { percent: 0.029, fixedCents: 30 }, // 2.9% + $0.30 — card-not-present
  card_present: { percent: 0.027, fixedCents: 5 }, // 2.7% + $0.05 — Tap to Pay / Terminal (verify before launch)
}

export interface ProcessingBreakdown {
  /** Barber's listed price, in cents. Routes 100% to the barber in BOTH modes. */
  serviceAmountCents: number
  /** Whether the client covers processing for THIS booking (stored immutably). */
  passedToClient: boolean
  /** The "Processing" line shown to the client. 0 when the barber absorbs. */
  processingAmountCents: number
  /** What the client's card is actually charged. */
  totalCents: number
  /** Rate components used at booking time — persisted so receipts never drift. */
  ratePercent: number
  rateFixedCents: number
  channel: FeeChannel
}

/**
 * The gross-up itself, kept separate from {@link computeProcessing} so the math
 * stays unit-tested and reviewable while PASS_THROUGH_ENABLED is false.
 *
 *   S = T - (T*percent + fixed)  =>  T = (S + fixed) / (1 - percent)
 */
export function grossUpForProcessing(serviceCents: number, channel: FeeChannel = "online"): number {
  const { percent, fixedCents } = STRIPE_RATES[channel]
  const service = Math.max(0, Math.round(serviceCents))
  if (service <= 0) return 0
  return Math.round((service + fixedCents) / (1 - percent))
}

/**
 * PASS-THROUGH IS OFF FOR NEW BOOKINGS (2026-10-01).
 *
 * The feature collected a line labelled "Processing" from the client, sized by
 * the gross-up above to cover Stripe's cut — and then transferred it to the
 * BARBER, because the full charge is transferred on a destination charge. But
 * Stripe's cut is debited from BOOTH's platform balance, not the barber's. So
 * the client paid a cost line that offset nobody's cost: a transfer from client
 * to barber wearing the name of a fee BOOTH was paying anyway.
 *
 * There is no wording that makes that disclosure true while the money moves
 * this way, so the option is off until the money moves differently (the line
 * would have to join BOOTH's application fee, not the transfer).
 *
 * Re-enabling requires BOTH:
 *   1. the collected amount actually offsetting BOOTH's processing expense, and
 *   2. checkout copy that names where it goes.
 *
 * EXISTING BOOKINGS ARE UNAFFECTED. Their totals were snapshotted from the
 * verified PaymentIntent at booking time (snapshotFromCharge + the processing_*
 * columns, migration 1013) and are never recomputed from this flag. Receipts for
 * past bookings still render the line the client really paid.
 */
export const PASS_THROUGH_ENABLED = false

/**
 * Compute the processing breakdown for a booking.
 *
 * Pass-through gross-up — solve for the total `T` whose Stripe fee leaves the
 * barber exactly their listed price `S`:
 *
 *   S = T − (T·percent + fixed)
 *   ⇒ T = (S + fixed) / (1 − percent)
 *   ⇒ processing = round(T) − S
 *
 * We round the client TOTAL to the nearest whole cent; the ≤1¢ rounding delta
 * sits with the platform (never the barber, never as margin), which keeps the
 * displayed "Processing" line identical to what Stripe actually deducts.
 *
 * Absorb mode returns processing = 0 and total = service price.
 *
 * @param serviceAmountCents  barber's listed price in cents (routes 100% to barber)
 * @param passToClient        the barber's `pass_processing_to_client` setting
 * @param channel             'online' (default) or 'card_present'
 */
export function computeProcessing(
  serviceAmountCents: number,
  passToClient: boolean,
  channel: FeeChannel = "online",
): ProcessingBreakdown {
  const service = Math.max(0, Math.round(serviceAmountCents))
  const { percent, fixedCents } = STRIPE_RATES[channel]

  // Absorb mode — and the $0 case, which must never pick up a phantom fixed fee
  // (a comped or free service charges the client nothing in either mode).
  // PASS_THROUGH_ENABLED short-circuits here so no caller can opt a NEW booking
  // into a line BOOTH cannot honestly describe. Existing bookings are unaffected:
  // their totals come from snapshotFromCharge, which reads the real charge.
  if (!PASS_THROUGH_ENABLED || !passToClient || service <= 0) {
    return {
      // Never report `passedToClient: true` with a zero line — a receipt that
      // claims a client covered processing while charging them nothing is a lie
      // in the other direction.
      serviceAmountCents: service,
      passedToClient: false,
      processingAmountCents: 0,
      totalCents: service,
      ratePercent: percent,
      rateFixedCents: fixedCents,
      channel,
    }
  }

  const totalCents = grossUpForProcessing(service, channel)
  return {
    serviceAmountCents: service,
    passedToClient: true,
    processingAmountCents: totalCents - service,
    totalCents,
    ratePercent: percent,
    rateFixedCents: fixedCents,
    channel,
  }
}

/**
 * Build the immutable snapshot from what the client's card was ACTUALLY
 * charged, rather than from the barber's live toggle.
 *
 * Use this at booking time. `chargedTotalCents` comes from the VERIFIED
 * PaymentIntent, so a toggle flipped between checkout and submit can never make
 * the stored receipt disagree with the real charge. Pass `chargedTotalCents = 0`
 * for an in-person booking (nothing was charged, so processing is 0).
 *
 * Anything charged above the base IS the processing the client paid — that's
 * the definition, not an estimate.
 */
export function snapshotFromCharge(
  baseCents: number,
  chargedTotalCents: number,
  channel: FeeChannel = "online",
): ProcessingBreakdown {
  const service = Math.max(0, Math.round(baseCents))
  const charged = Math.max(0, Math.round(chargedTotalCents))
  const { percent, fixedCents } = STRIPE_RATES[channel]
  // A charge below the base (shouldn't happen — the PI is verified against it)
  // must never produce a negative processing line.
  const processing = Math.max(0, charged - service)
  return {
    serviceAmountCents: service,
    passedToClient: processing > 0,
    processingAmountCents: processing,
    totalCents: service + processing,
    ratePercent: percent,
    rateFixedCents: fixedCents,
    channel,
  }
}

/**
 * Map a breakdown to the columns persisted on the appointment at creation time.
 * These are IMMUTABLE after creation — receipts render from them, not from the
 * barber's live toggle (see spec §3.4). Mirrors migration 089.
 */
export function processingToBookingFields(b: ProcessingBreakdown) {
  return {
    processing_passed_to_client: b.passedToClient,
    processing_amount_cents: b.processingAmountCents,
    processing_rate_percent: b.ratePercent,
    processing_rate_fixed_cents: b.rateFixedCents,
  }
}

/** Convenience for display layers: dollars in/out. */
export function computeProcessingDollars(serviceDollars: number, passToClient: boolean, channel: FeeChannel = "online") {
  const b = computeProcessing(Math.round(serviceDollars * 100), passToClient, channel)
  return {
    service: b.serviceAmountCents / 100,
    processing: b.processingAmountCents / 100,
    total: b.totalCents / 100,
    passedToClient: b.passedToClient,
  }
}

// Bookings is free for barbers as of 2026-09-26 — the $25/mo subscription, its
// promo codes and its paywall are gone (lib/bookings-access.ts). Nothing reads
// a price here any more. The per-transaction processing fee above is unchanged
// and is a different thing: a cut of card processing on real money, not a
// charge for using Bookings.

// Brand-aligned statement for pricing/storefront copy.
//
// The previous wording ("BOOTH takes no cut of what a barber earns") was simply
// untrue of Bookings — the application fee is a cut of what the barber earns.
// It is the client who pays no BOOTH fee. Say that instead.
export const BOOKINGS_FEE_TAGLINE =
  `BOOTH never adds a booking fee to what a client pays. BOOTH's ${BOOKINGS_FEE_LABEL} (minimum ${BOOKINGS_FEE_MINIMUM_LABEL}) comes out of the barber's earnings, and BOOTH covers standard card processing out of it. By default the client pays exactly the listed price.`
